import React, { useEffect, useState, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import api from '../api/client'
import type { ShiftAssignment } from '../types'
import { Calendar, Clock, MapPin, MessageSquare, Users } from 'lucide-react'
import { isAfter, startOfDay } from 'date-fns'
import { Alert, Button, StatusBadge } from '@rallyshift/design-system'
import { formatDayDateToLocal, formatTimeRangeToLocal, getTimezoneAbbreviation } from '../utils/timezone'
import { statusLabel } from '../utils/status'
import ContactAdminModal from '../components/ContactAdminModal'
import { useLocale } from '../contexts/LocaleContext'
import { useOpenConversation } from '../hooks/useOpenConversation'

const MyShiftsPage: React.FC = () => {
  const { user, volunteerMetadata, isVolunteer, isAdmin } = useAuth()
  const { t } = useLocale()
  const { open: openChat, opening, error: chatError } = useOpenConversation()
  const [assignments, setAssignments] = useState<ShiftAssignment[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [isContactAdminModalOpen, setIsContactAdminModalOpen] = useState(false)

  const fetchMyShifts = useCallback(async () => {
    if (!user) return

    try {
      setLoading(true)
      // Check if user is a volunteer or admin
      if (!isVolunteer && !isAdmin) {
        setError('You are not registered as a volunteer. Please contact an admin.')
        return
      }

      // Now fetch the shift assignments
      const data = await api.shifts.getMyAssignments()
      setAssignments(data || [])
    } catch (err: any) {
      console.error('Error fetching shifts:', err)
      setError(err.message || 'Failed to fetch your shifts')
    } finally {
      setLoading(false)
    }
  }, [user, isVolunteer, isAdmin])

  useEffect(() => {
    if (user && (isVolunteer || isAdmin)) {
      fetchMyShifts()
    }
  }, [user, isVolunteer, isAdmin, fetchMyShifts])

  const isUpcoming = (startTime: string) => {
    return isAfter(new Date(startTime), new Date())
  }

  const isToday = (startTime: string) => {
    const today = startOfDay(new Date())
    const shiftDate = startOfDay(new Date(startTime))
    return today.getTime() === shiftDate.getTime()
  }

  const handleStatusChange = async (assignmentId: string, newStatus: string) => {
    try {
      await api.shifts.updateAssignment(assignmentId, { status: newStatus })

      if (newStatus === 'cancelled') {
        const assignment = assignments.find((a: any) => a.id === assignmentId)
        if (assignment?.shift && user) {
          try {
            await api.notifications.send('shift_removed', {
              volunteerEmail: user.email || assignment.email,
              volunteerFirstName: volunteerMetadata?.first_name || assignment.first_name || '',
              volunteerLastName: volunteerMetadata?.last_name || assignment.last_name || '',
              shiftTitle: assignment.shift.title,
              shiftDescription: assignment.shift.description,
              shiftStartTime: assignment.shift.start_time,
              shiftEndTime: assignment.shift.end_time,
              shiftLocation: assignment.shift.location,
              eventTitle: assignment.shift.event?.title,
              removedAt: new Date().toISOString(),
              formattedStartTime: new Date(assignment.shift.start_time).toLocaleString('nl-NL'),
              formattedEndTime: new Date(assignment.shift.end_time).toLocaleString('nl-NL'),
              formattedRemovedDate: new Date().toLocaleDateString('nl-NL')
            })
          } catch (emailError) {
            console.error('Error sending shift removal email:', emailError)
          }
        }
      }

      await fetchMyShifts()
    } catch (err: any) {
      setError(err.message || 'Failed to update shift status')
    }
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-rs-spin rounded-full border-2.5 border-tenant-brand border-r-transparent" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="font-display text-display-lg text-text-primary">Mijn diensten</h1>
          <p className="mt-2 text-md text-text-secondary">
            Bekijk en beheer je toegewezen vrijwilligersdiensten.
          </p>
        </div>
        <div className="mt-4 flex items-center gap-4 sm:ml-16 sm:mt-0 sm:flex-none">
          <span className="text-sm text-text-muted">{assignments.length} diensten</span>
          <Button variant="secondary" onClick={fetchMyShifts} loading={loading}>
            Vernieuwen
          </Button>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {chatError && <Alert variant="danger">{chatError}</Alert>}

      <div className="overflow-hidden rounded-lg border border-border bg-surface-card shadow-card">
        {assignments.length === 0 ? (
          <div className="py-12 text-center">
            <Calendar className="mx-auto h-12 w-12 text-text-muted" />
            <h3 className="mt-2 text-md font-semibold text-text-primary">
              Geen diensten toegewezen
            </h3>
            <p className="mt-1 text-sm text-text-secondary">
              Zodra je bent ingepland, verschijnt de dienst hier.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {assignments.map((assignment) => (
              <li key={assignment.id}>
                <div className="px-4 py-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
                        <h3 className="truncate text-lg font-semibold text-text-primary">
                          {assignment.shift?.title}
                        </h3>
                        <div className="flex flex-wrap items-center gap-2">
                          <StatusBadge
                            dimension="assignment"
                            status={assignment.status}
                            label={statusLabel('assignment', assignment.status)}
                          />
                          {assignment.shift && isUpcoming(assignment.shift.start_time) && isToday(assignment.shift.start_time) && (
                            <span className="rounded-pill bg-status-warning-bg px-3 py-1 text-[13px] font-semibold text-status-warning-text">
                              Vandaag
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="mt-2 flex flex-col space-y-1 text-sm text-text-secondary sm:flex-row sm:items-center sm:space-x-6 sm:space-y-0">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1 flex-shrink-0" />
                          {assignment.shift && formatDayDateToLocal(assignment.shift.start_time, 'MMM d, yyyy')}
                          <span className="ml-1 text-xs text-text-muted">({getTimezoneAbbreviation()})</span>
                        </div>
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 mr-1 flex-shrink-0" />
                          {assignment.shift && formatTimeRangeToLocal(assignment.shift.start_time, assignment.shift.end_time)}
                        </div>
                        <div className="flex items-center">
                          <MapPin className="h-4 w-4 mr-1 flex-shrink-0" />
                          <span className="truncate">{assignment.shift?.location}</span>
                        </div>
                        <div className="flex items-center">
                          <Users className="h-4 w-4 mr-1 flex-shrink-0" />
                          {assignment.shift?.current_volunteers}/{assignment.shift?.max_volunteers} vrijwilligers
                        </div>
                      </div>
                      {assignment.shift?.event && (
                        <div className="mt-1 text-sm text-text-secondary">
                          Evenement: {assignment.shift.event.title}
                        </div>
                      )}
                      {assignment.shift?.description && (
                        <div className="mt-2 text-sm text-text-secondary">
                          {assignment.shift.description}
                        </div>
                      )}
                      {assignment.notes && (
                        <div className="mt-2 rounded-md bg-surface-sunken p-2 text-sm text-text-secondary">
                          <strong className="text-text-primary">Opmerkingen:</strong> {assignment.notes}
                        </div>
                      )}
                    </div>
                    <div className="flex-shrink-0 flex flex-wrap items-center gap-2 sm:gap-2 mt-3 sm:mt-0">
                      {assignment.status !== 'cancelled' && assignment.shift && (
                        <Button
                          variant="secondary"
                          icon={<MessageSquare className="h-4 w-4" />}
                          loading={opening}
                          onClick={() => openChat(() => api.messaging.forShift(assignment.shift!.id))}
                        >
                          {t('messages.teamChat')}
                        </Button>
                      )}
                      {assignment.status === 'assigned' && assignment.shift && isUpcoming(assignment.shift.start_time) && (
                        <>
                          {/* Only admins can confirm on someone's behalf. */}
                          {isAdmin && (
                            <Button
                              variant="tenant"
                              onClick={() => handleStatusChange(assignment.id, 'confirmed')}
                            >
                              Bevestigen
                            </Button>
                          )}
                          {/* Admins cancel directly; volunteers go through the organiser. */}
                          <Button
                            variant="secondary"
                            onClick={() =>
                              isAdmin
                                ? handleStatusChange(assignment.id, 'cancelled')
                                : setIsContactAdminModalOpen(true)
                            }
                          >
                            Annuleren
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <ContactAdminModal
        isOpen={isContactAdminModalOpen}
        onClose={() => setIsContactAdminModalOpen(false)}
      />
    </div>
  )
}

export default MyShiftsPage
